import React, { useState, useRef } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import type { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BASE_URL } from '@/config/constants';

interface ImageUploaderProps {
  onImageUploaded: (url: string) => void;
  aspectRatio?: number;
  maxSizeMB?: number;
  className?: string;
  /** When provided, renders a styled button trigger instead of a bare file input. */
  buttonLabel?: React.ReactNode;
  /** Round crop selection (for avatars). */
  circularCrop?: boolean;
  /** Custom trigger: clicking the children opens the file picker. Takes precedence over buttonLabel. */
  children?: React.ReactNode;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageUploaded,
  aspectRatio = 16 / 9,
  maxSizeMB = 1,
  className = "",
  buttonLabel,
  circularCrop = false,
  children
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [crop, setCrop] = useState<Crop | undefined>();
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      return;
    }

    // Validate file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File size must be less than ${maxSizeMB}MB.`);
      return;
    }

    setError('');

    // Create a temporary URL for the image; the initial crop is computed
    // in handleImageLoad once the image has actually rendered, so the
    // selection is always centered and sized to the displayed image.
    const reader = new FileReader();
    reader.onload = () => {
      setCrop(undefined);
      setTempImage(reader.result as string);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  // Center a 90%-wide crop with the desired aspect ratio, relative to the
  // *rendered* image size (react-image-crop coordinates are display-based).
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(
      centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, aspectRatio, width, height),
        width,
        height
      )
    );
  };

  const getCroppedImg = (image: HTMLImageElement, crop: Crop): Promise<Blob> => {
    // Normalize %-unit crops (initial centered crop) to display pixels.
    const pxCrop = crop.unit === '%'
      ? {
          x: (crop.x / 100) * image.width,
          y: (crop.y / 100) * image.height,
          width: (crop.width / 100) * image.width,
          height: (crop.height / 100) * image.height,
        }
      : crop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Export at the image's natural resolution for maximum quality.
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(pxCrop.width * scaleX);
    canvas.height = Math.round(pxCrop.height * scaleY);
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return Promise.reject(new Error('No 2d context'));
    }

    ctx.drawImage(
      image,
      pxCrop.x * scaleX,
      pxCrop.y * scaleY,
      pxCrop.width * scaleX,
      pxCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const uploadImage = async () => {
    if (!imageRef.current || !crop) {
      setError('Please select an area to crop');
      return;
    }

    try {
      setUploading(true);
      setError('');

      // Get the cropped image blob
      const croppedImageBlob = await getCroppedImg(imageRef.current, crop);

      // Create FormData and append the cropped image
      const formData = new FormData();
      formData.append('file', croppedImageBlob, 'cropped-image.jpg');

      // Upload the cropped image
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(BASE_URL + '/api/images/upload', formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      // Close the crop dialog and reset the state
      setCropDialogOpen(false);
      setTempImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call the callback with the uploaded image URL
      onImageUploaded(res.data.url);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'Image upload failed.');
      } else {
        setError('Image upload failed.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={className}>
      <input
        type="file"
        accept="image/png,image/jpeg"
        onChange={handleFileChange}
        ref={fileInputRef}
        className={children || buttonLabel ? 'hidden' : 'w-full border p-2 rounded'}
      />
      {children ? (
        <button
          type="button"
          className="block cursor-pointer bg-transparent border-0 p-0 text-left"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Upload image"
        >
          {children}
        </button>
      ) : buttonLabel ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => fileInputRef.current?.click()}
        >
          {buttonLabel}
        </Button>
      ) : null}
      {error && <div className="text-red-500 mt-2">{error}</div>}

      <Dialog open={cropDialogOpen} onOpenChange={(open) => {
        setCropDialogOpen(open);
        if (!open) {
          setTempImage(null);
          setCrop(undefined);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Crop image</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex justify-center bg-muted/30 rounded-lg p-3 overflow-hidden">
              {tempImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c: Crop) => setCrop(c)}
                  aspect={aspectRatio}
                  circularCrop={circularCrop}
                  keepSelection
                  className="max-w-full"
                >
                  <img
                    ref={imageRef}
                    src={tempImage}
                    alt="crop preview"
                    onLoad={handleImageLoad}
                    className="max-w-full max-h-[55vh] object-contain"
                  />
                </ReactCrop>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Drag to reposition, or resize from the corners.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCropDialogOpen(false);
                  setTempImage(null);
                  setCrop(undefined);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                Cancel
              </Button>
              <Button onClick={uploadImage} disabled={uploading || !crop}>
                {uploading ? 'Uploading...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploader;